import { format } from 'date-fns';
import { useCallback, useMemo, useState } from 'react';
import { T, DateTime, Var, useGT } from 'gt-react';
import { Action, Subject } from '@erp/authz';
import { parseDateOnly } from '@/utils/dateUtils';
import { SystemEntityInline } from '@/components/system-entities/inline-ui';
import { SystemEntity } from '@erp/shared-types';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { usePaymentAssociations } from '@/api/rpc/hooks/core/useEntityAssociationsFiltered';
import type { Payment, SimulatePaymentProviderStatusInput } from '@erp/rpc';
import { SendRemittanceDialog } from './SendRemittanceDialog';
import { Badge } from '@/components/ui/badge';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import {
  useExecutePayment,
  useRecordExternalPayment,
  useRemoveInvoiceFromPayment,
  useRescheduleInvoice,
  useSimulatePaymentProviderStatus,
  useVoidPayment,
} from '@/api/rpc/hooks/finance/usePayments';
import { useRemittanceEmails } from '@/api/rpc/hooks/finance/useRemittanceEmails';
import { useUpdatePayment } from '@/api/rpc/hooks/finance/useUpdatePayment';
import { useRevokeApprovalRequest } from '@/api/rpc/hooks/finance/useRevokeApprovalRequest';
import { useCan } from '@/hooks/useCan';
import { PaymentAccountSelector } from './PaymentAccountSelector';
import { SupplierBankSelector } from './SupplierBankSelector';
import { PaymentAiReviewEntry } from './PaymentAiReviewEntry';
import { PaymentApprovalsSection } from './PaymentApprovalsSection';
import { InvoicesSection } from './InvoicesSection';
import {
  usePaymentInlineImage,
  PaymentImageViewerPanel,
} from './PaymentImageViewer';
import {
  formatCurrency,
  formatStatusLabel,
  getProviderStatusVariant,
  getStatusVariant,
} from './payment-formatting';
import { usePaymentAccounts } from '@/api/rpc/hooks/integrations/usePaymentAccounts';
import { DatePicker } from '@/components/common/date-pickers/date-picker';
import { Textarea } from '@/components/ui/textarea';
import {
  SpinnerIcon,
  CheckIcon,
  DotsThreeVerticalIcon,
  WarningIcon,
} from '@phosphor-icons/react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export interface PaymentDetailsProps {
  payment: Payment;
}

/** Which destructive/confirm dialog is currently open, if any. */
type ConfirmAction = 'void' | 'record-external' | 'execute' | 'revoke' | null;

export function PaymentDetailsSkeleton() {
  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
        {/* Amount Section */}
        <div className="bg-muted/50 rounded-lg p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-2">
              <Skeleton className="h-3 w-16" />
              <Skeleton className="h-9 w-40" />
            </div>
            <Skeleton className="h-5 w-20 rounded-full" />
          </div>
        </div>

        <Separator />

        {/* Payment Details */}
        <div className="space-y-4">
          <Skeleton className="h-3 w-28" />
          <div className="grid grid-cols-2 gap-x-4 gap-y-4">
            {Array.from({ length: 4 }, (_, i) => (
              <div key={i} className="space-y-1.5">
                <Skeleton className="h-3 w-20" />
                <Skeleton className="h-4 w-32" />
              </div>
            ))}
          </div>
        </div>

        <Separator />

        {/* Invoices */}
        <div className="space-y-3">
          <Skeleton className="h-3 w-24" />
          <Skeleton className="h-32 w-full rounded-md" />
        </div>

        <Separator />

        {/* Approvals */}
        <div className="space-y-3">
          <Skeleton className="h-3 w-20" />
          <Skeleton className="h-10 w-full rounded-md" />
        </div>
      </div>

      {/* Footer */}
      <div className="border-t px-6 py-3 bg-muted/20">
        <div className="flex items-center justify-between">
          <Skeleton className="h-3 w-40" />
          <Skeleton className="h-3 w-40" />
        </div>
      </div>
    </div>
  );
}

export function PaymentDetails({ payment }: PaymentDetailsProps) {
  const gt = useGT();
  const { associations } = usePaymentAssociations(payment.id);
  const { emails: remittanceEmails } = useRemittanceEmails(payment.id);
  const simulateProviderStatus = useSimulatePaymentProviderStatus();
  const voidPayment = useVoidPayment();
  const executePayment = useExecutePayment();
  const recordExternalPayment = useRecordExternalPayment();
  const removeInvoice = useRemoveInvoiceFromPayment();
  const rescheduleInvoice = useRescheduleInvoice();
  const updatePayment = useUpdatePayment();
  const revokeApprovalRequest = useRevokeApprovalRequest();
  const [canExecutePayment] = useCan(Action.Execute, Subject.APInvoicePayment);
  const [canEditPayment] = useCan(Action.Update, Subject.APInvoicePayment);
  // Supplier bank details are supplier data — same permission that gates the
  // supplier-info tab on the companies page.
  const [canReadSupplier] = useCan(Action.Read, Subject.Supplier);
  const { verifiedAccounts } = usePaymentAccounts();
  const isEditable =
    payment.status === 'in_review' && !payment.hasApprovalRequest;
  const [memoSaveState, setMemoSaveState] = useState<
    'idle' | 'saving' | 'saved'
  >('idle');
  const saveMemo = useCallback(
    async (value: string) => {
      const trimmed = value.trim();
      if (trimmed === (payment.memo ?? '')) return;
      setMemoSaveState('saving');
      try {
        await updatePayment.mutateAsync({
          id: payment.id,
          data: { memo: trimmed || undefined },
        });
        setMemoSaveState('saved');
        setTimeout(() => setMemoSaveState('idle'), 1500);
      } catch {
        setMemoSaveState('idle');
      }
    },
    [payment.id, payment.memo, updatePayment],
  );
  const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null);
  const [removeInvoiceId, setRemoveInvoiceId] = useState<string | null>(null);
  const inlineImage = usePaymentInlineImage(payment);
  const invoiceDetails = payment.invoices ?? [];
  const externalPayment = payment.externalPayment;

  const closeConfirm = useCallback(() => setConfirmAction(null), []);

  // Approval-request emails are shown in the Approvals section, not the Emails section
  const approvalEmailIdSet = useMemo(
    () => new Set(payment.approvalRequestEmailIds ?? []),
    [payment.approvalRequestEmailIds],
  );

  // Only show non-approval outbound emails in the Emails section
  const outboundEmailIds = useMemo(() => {
    return associations
      .filter(
        (assoc) =>
          assoc.entity.entityType === SystemEntity.OUTBOUND_EMAIL &&
          !approvalEmailIdSet.has(assoc.entity.entityId),
      )
      .map((assoc) => assoc.entity.entityId);
  }, [associations, approvalEmailIdSet]);

  // Stable, content-based keys for remittance advice rows (lines have no IDs;
  // duplicates get an occurrence suffix so keys stay unique).
  const remittanceRows = useMemo(() => {
    const counts = new Map<string, number>();
    return (externalPayment?.remittanceAdvice ?? []).map((line) => {
      const text = line.join(' | ');
      const occurrence = counts.get(text) ?? 0;
      counts.set(text, occurrence + 1);
      return {
        key: occurrence === 0 ? text : `${text}#${occurrence}`,
        text,
      };
    });
  }, [externalPayment?.remittanceAdvice]);

  const isDraft = payment.status === 'in_review' && !payment.hasApprovalRequest;

  // The supplier bank can change while the payment can still (re-)execute:
  // in_review, or failed without a provider reference (retry re-snapshots).
  const canChangeSupplierBank =
    payment.status === 'in_review' ||
    (payment.status === 'failed' && !payment.referenceNumber);

  function handleRemoveInvoiceConfirm() {
    if (!removeInvoiceId) return;
    removeInvoice.mutate(
      { paymentId: payment.id, invoiceId: removeInvoiceId },
      {
        onSuccess: () => {
          setRemoveInvoiceId(null);
        },
      },
    );
  }

  function handleVoidConfirm() {
    voidPayment.mutate({ id: payment.id }, { onSuccess: closeConfirm });
  }

  function handleExecuteConfirm() {
    executePayment.mutate({ id: payment.id }, { onSuccess: closeConfirm });
  }

  function handleRecordExternalConfirm() {
    recordExternalPayment.mutate(
      { id: payment.id },
      { onSuccess: closeConfirm },
    );
  }

  // Recording a payment as externally paid is an Update action (no money moves
  // through the system), available from failed/pending states per the server.
  const canRecordExternal =
    canEditPayment && (payment.canRecordExternal ?? false);

  const canRetryPayment = externalPayment?.allowedActions.canRetry ?? false;
  // A `failed` payment is a true retry; an `approved` one stalled before it ever
  // ran, so "Execute" reads more accurately there.
  const executeButtonLabel =
    payment.status === 'failed' ? gt('Retry Payment') : gt('Execute Payment');

  function handleSandboxStatusClick(
    status: SimulatePaymentProviderStatusInput['status'],
  ) {
    simulateProviderStatus.mutate({
      id: payment.id,
      status,
    });
  }

  // Send remittance dialog
  const [isRemittanceDialogOpen, setIsRemittanceDialogOpen] = useState(false);

  // Derive supplier from invoice details
  const firstInvoiceWithSupplier = invoiceDetails.find(
    (inv) => inv.supplier || inv.supplierId,
  );
  const supplier = firstInvoiceWithSupplier?.supplier ?? null;
  const supplierId = firstInvoiceWithSupplier?.supplierId ?? null;

  return (
    <>
      <div className="flex flex-col h-full">
        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
          {/* Amount Section */}
          <div className="bg-muted/50 rounded-lg p-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                  <T>Amount</T>
                </h3>
                <p className="text-3xl font-bold">
                  {Number(payment.amount).toLocaleString(undefined, {
                    style: 'currency',
                    currency: payment.currency || 'USD',
                  })}
                </p>
              </div>

              <div className="flex flex-col items-end gap-2">
                <Badge variant={getStatusVariant(payment.status)}>
                  {formatStatusLabel(payment.status)}
                </Badge>

                {(externalPayment || canRecordExternal) && (
                  <div className="flex flex-wrap justify-end gap-2">
                    {externalPayment &&
                      canExecutePayment &&
                      canRetryPayment && (
                        <Button
                          size="sm"
                          onClick={() => setConfirmAction('execute')}
                          disabled={executePayment.isPending}
                        >
                          {executePayment.isPending
                            ? gt('Submitting...')
                            : executeButtonLabel}
                        </Button>
                      )}
                    {externalPayment && canExecutePayment && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setConfirmAction('void')}
                        disabled={
                          !externalPayment.allowedActions.canVoid ||
                          voidPayment.isPending
                        }
                      >
                        {voidPayment.isPending
                          ? gt('Voiding...')
                          : gt('Void Payment')}
                      </Button>
                    )}
                    {canRecordExternal && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={recordExternalPayment.isPending}
                            aria-label={gt('More payment actions')}
                          >
                            <DotsThreeVerticalIcon className="size-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => setConfirmAction('record-external')}
                          >
                            <T>Record as externally paid</T>
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          <Separator />

          {/* Payment Details (merged with Provider) */}
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                <T>Payment Details</T>
              </h3>
              {externalPayment?.status && (
                <Badge
                  variant={getProviderStatusVariant(externalPayment.status)}
                >
                  {formatStatusLabel(externalPayment.status)}
                </Badge>
              )}
            </div>
            <div
              className={
                externalPayment
                  ? 'grid gap-6 md:grid-cols-2'
                  : 'grid gap-6 grid-cols-1'
              }
            >
              {/* Payment column */}
              <div className="space-y-4">
                {externalPayment && (
                  <h4 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                    <T>Payment</T>
                  </h4>
                )}
                <div className="grid grid-cols-2 gap-x-4 gap-y-4">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">
                      <T>Payment Date</T>
                    </p>
                    {isDraft && canEditPayment ? (
                      <DatePicker
                        value={parseDateOnly(payment.paymentDate) ?? undefined}
                        onChange={(date) => {
                          if (!date) return;
                          const dateStr = format(date, 'yyyy-MM-dd');
                          if (dateStr === payment.paymentDate) return;
                          updatePayment.mutate({
                            id: payment.id,
                            data: { paymentDate: dateStr },
                          });
                        }}
                      />
                    ) : (
                      <p className="text-sm font-medium">
                        <T>
                          <DateTime options={{ dateStyle: 'long' }}>
                            {parseDateOnly(payment.paymentDate)}
                          </DateTime>
                        </T>
                      </p>
                    )}
                  </div>

                  <div>
                    <p className="text-xs text-muted-foreground mb-1">
                      <T>Method</T>
                    </p>
                    <p className="text-sm font-medium uppercase">
                      {payment.method}
                    </p>
                  </div>

                  <div className="col-span-2">
                    <p className="text-xs text-muted-foreground mb-1">
                      <T>Funding Account</T>
                    </p>
                    {isEditable && canEditPayment ? (
                      <PaymentAccountSelector
                        paymentId={payment.id}
                        currentAccountId={payment.paymentAccountId}
                      />
                    ) : (
                      <p className="text-sm font-medium">
                        {payment.executionFundingInstitutionName
                          ? `${payment.executionFundingInstitutionName} (****${payment.executionFundingAccountMask})`
                          : (() => {
                              const account = verifiedAccounts.find(
                                (a) => a.id === payment.paymentAccountId,
                              );
                              return account
                                ? `${account.name} · ${account.institutionName} (****${account.accountMask})`
                                : gt('Default');
                            })()}
                      </p>
                    )}
                  </div>

                  {supplierId && canReadSupplier && (
                    <div className="col-span-2">
                      <p className="text-xs text-muted-foreground mb-1">
                        <T>Supplier Bank</T>
                      </p>
                      {/* Editable even with a pending approval request — the
                          bank doesn't change what approvers signed off on.
                          Failed payments that never reached the provider are
                          editable too; retry re-snapshots from the new bank. */}
                      <SupplierBankSelector
                        paymentId={payment.id}
                        supplierId={supplierId}
                        currentDetailsId={payment.supplierPaymentDetailsId}
                        editable={canChangeSupplierBank && canEditPayment}
                        warnIfMissing={canChangeSupplierBank}
                      />
                    </div>
                  )}

                  <div>
                    <p className="text-xs text-muted-foreground mb-1">
                      <T>Status</T>
                    </p>
                    <p className="text-sm font-medium">
                      {formatStatusLabel(payment.status)}
                    </p>
                  </div>

                  {payment.referenceNumber && (
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">
                        <T>Reference Number</T>
                      </p>
                      <p className="text-sm font-medium font-mono">
                        {payment.referenceNumber}
                      </p>
                    </div>
                  )}

                  {(payment.memo || (isDraft && canEditPayment)) && (
                    <div className="col-span-2">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-xs text-muted-foreground">
                          <T>Memo</T>
                        </p>
                        {memoSaveState === 'saving' && (
                          <SpinnerIcon className="size-3 animate-spin text-muted-foreground" />
                        )}
                        {memoSaveState === 'saved' && (
                          <CheckIcon className="size-3 text-green-600" />
                        )}
                      </div>
                      {isDraft && canEditPayment ? (
                        <Textarea
                          key={payment.memo ?? ''}
                          defaultValue={payment.memo ?? ''}
                          onBlur={(e) => void saveMemo(e.target.value)}
                          rows={2}
                          className="text-sm"
                          placeholder={gt('Add a memo...')}
                        />
                      ) : (
                        <p className="text-sm leading-relaxed">
                          {payment.memo}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Provider column */}
              {externalPayment && (
                <div className="space-y-4 md:border-l md:pl-6">
                  <h4 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                    <T>Provider</T>
                  </h4>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-4">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">
                        <T>Provider</T>
                      </p>
                      <p className="text-sm font-medium capitalize">
                        {externalPayment.provider}
                      </p>
                    </div>

                    {externalPayment.eta && (
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">
                          <T>Estimated Arrival</T>
                        </p>
                        <p className="text-sm font-medium">
                          <T>
                            <DateTime options={{ dateStyle: 'long' }}>
                              {parseDateOnly(externalPayment.eta)}
                            </DateTime>
                          </T>
                        </p>
                      </div>
                    )}

                    {externalPayment.cancellable !== null && (
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">
                          <T>Cancellable</T>
                        </p>
                        <p className="text-sm font-medium">
                          {externalPayment.cancellable ? gt('Yes') : gt('No')}
                        </p>
                      </div>
                    )}

                    {externalPayment.externalPaymentId && (
                      <div className="col-span-2">
                        <p className="text-xs text-muted-foreground mb-1">
                          <T>Provider Payment ID</T>
                        </p>
                        <p className="text-sm font-medium font-mono break-all">
                          {externalPayment.externalPaymentId}
                        </p>
                      </div>
                    )}

                    {externalPayment.voidBlockedReason &&
                      !externalPayment.allowedActions.canVoid && (
                        <div className="col-span-2 rounded-md border px-3 py-2 text-sm text-muted-foreground">
                          {externalPayment.voidBlockedReason}
                        </div>
                      )}

                    {(inlineImage.canPreviewImage ||
                      inlineImage.canViewCheckImage) && (
                      <div className="col-span-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={inlineImage.handleImageButtonClick}
                          disabled={
                            inlineImage.canPreviewImage &&
                            inlineImage.isPreviewPending
                          }
                        >
                          {inlineImage.isPreviewLoading
                            ? gt('Loading Preview...')
                            : inlineImage.imageButtonLabel}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {externalPayment?.providerError && (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                {externalPayment.providerError}
              </div>
            )}

            {externalPayment?.lastExecutionError && (
              <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
                <p className="font-medium">
                  <T>Last execution error</T>
                </p>
                <p>{externalPayment.lastExecutionError}</p>
              </div>
            )}

            <PaymentImageViewerPanel
              paymentId={payment.id}
              viewer={inlineImage.viewer}
            />

            {externalPayment?.sandboxMode &&
              externalPayment.sandboxNextStatuses &&
              externalPayment.sandboxNextStatuses.length > 0 &&
              canExecutePayment && (
                <div className="space-y-2">
                  <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    <T>Sandbox Status</T>
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {externalPayment.sandboxNextStatuses.map((status) => (
                      <Button
                        key={status}
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          handleSandboxStatusClick(
                            status as SimulatePaymentProviderStatusInput['status'],
                          )
                        }
                        disabled={simulateProviderStatus.isPending}
                      >
                        {simulateProviderStatus.isPending
                          ? gt('Updating...')
                          : gt('Set {status}', { status })}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

            {remittanceRows.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  <T>Remittance Advice</T>
                </h4>
                <div className="flex flex-col gap-2">
                  {remittanceRows.map((row) => (
                    <div
                      key={row.key}
                      className="rounded-md border px-3 py-2 font-mono text-xs"
                    >
                      {row.text}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Associated Invoices */}
          {invoiceDetails.length > 0 && (
            <>
              <Separator />
              <InvoicesSection
                invoices={invoiceDetails}
                payment={payment}
                showActions={isDraft && canEditPayment}
                removePending={removeInvoice.isPending}
                reschedulePending={rescheduleInvoice.isPending}
                onRemove={setRemoveInvoiceId}
                onReschedule={(invoiceId, newDate) => {
                  rescheduleInvoice.mutate({
                    paymentId: payment.id,
                    invoiceId,
                    newPaymentDate: newDate,
                  });
                }}
              />
            </>
          )}

          <Separator />

          {/* Approvals Section */}
          <div className="space-y-3">
            <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              <T>Approvals</T>
            </h3>

            {payment.hasApprovalRequest && payment.status === 'in_review' && (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm text-amber-900">
                    <T>
                      Approval request pending — editing is disabled until
                      revoked.
                    </T>
                  </p>
                  {canEditPayment && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="shrink-0"
                      onClick={() => setConfirmAction('revoke')}
                      disabled={revokeApprovalRequest.isPending}
                    >
                      <T>Revoke Request</T>
                    </Button>
                  )}
                </div>
              </div>
            )}

            <PaymentAiReviewEntry payment={payment} />
            <PaymentApprovalsSection paymentId={payment.id} />
          </div>

          {/* Financial Transaction */}
          {payment.financialTransaction && (
            <>
              <Separator />
              <div className="space-y-3">
                <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  <T>Financial Transaction</T>
                </h3>
                <SystemEntityInline
                  entityType={SystemEntity.FINANCIAL_TRANSACTION}
                  entityId={payment.financialTransaction.id}
                />
              </div>
            </>
          )}

          {/* Emails (remittance + outbound) */}
          <Separator />
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                {outboundEmailIds.length > 0
                  ? gt('Emails ({count})', {
                      count: String(outboundEmailIds.length),
                    })
                  : gt('Emails')}
              </h3>
              {canEditPayment && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsRemittanceDialogOpen(true)}
                >
                  {remittanceEmails.length > 0
                    ? gt('Resend Remittance')
                    : gt('Send Remittance')}
                </Button>
              )}
            </div>
            {outboundEmailIds.length > 0 && (
              <div className="flex flex-col gap-2">
                {outboundEmailIds.map((emailId) => (
                  <SystemEntityInline
                    key={emailId}
                    entityType={SystemEntity.OUTBOUND_EMAIL}
                    entityId={emailId}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer with Timestamps */}
        <div className="border-t px-6 py-3 bg-muted/20">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <div>
              <span className="font-medium">
                <T>Created:</T>
              </span>{' '}
              {format(new Date(payment.createdAt), 'MMM d, yyyy, h:mm a')}
            </div>
            <div>
              <span className="font-medium">
                <T>Updated:</T>
              </span>{' '}
              {format(new Date(payment.updatedAt), 'MMM d, yyyy, h:mm a')}
            </div>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={confirmAction === 'void'}
        onClose={closeConfirm}
        onConfirm={handleVoidConfirm}
        title={gt('Void payment?')}
        message={
          externalPayment?.externalPaymentId
            ? gt('This will void provider payment {id}.', {
                id: externalPayment.externalPaymentId,
              })
            : gt('This will void the external payment.')
        }
        confirmText={gt('Void payment')}
        loading={voidPayment.isPending}
        dangerous
      />

      <ConfirmDialog
        open={confirmAction === 'record-external'}
        onClose={closeConfirm}
        onConfirm={handleRecordExternalConfirm}
        title={gt('Record as externally paid?')}
        message={gt(
          'This marks the payment as paid without using the provider — use it when the supplier was paid manually (e.g. a bank ACH transfer). Linked invoices will be updated to reflect the payment.',
        )}
        dangerous
        confirmText={gt('Record as paid')}
        loading={recordExternalPayment.isPending}
      >
        <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
          <WarningIcon className="h-5 w-5 shrink-0 mt-0.5" weight="fill" />
          <div className="space-y-1">
            <p className="font-medium">
              <T context="warning heading when recording an external payment">
                No money moves through the system, and this can&apos;t be
                undone.
              </T>
            </p>
            <p>
              <T context="warning detail when recording an external payment">
                Only do this once you&apos;ve confirmed the supplier was
                actually paid the full{' '}
                <Var>
                  {formatCurrency(payment.amount, payment.currency || 'USD')}
                </Var>{' '}
                outside the system. Recording it here will not send any funds —
                if the payment hasn&apos;t truly been made, the invoices will be
                wrongly marked as paid.
              </T>
            </p>
          </div>
        </div>
      </ConfirmDialog>

      <ConfirmDialog
        open={confirmAction === 'execute'}
        onClose={closeConfirm}
        onConfirm={handleExecuteConfirm}
        title={
          payment.status === 'failed'
            ? gt('Retry payment?')
            : gt('Execute payment?')
        }
        message={gt(
          'This will submit a {amount} ACH payment to the provider. If a previous attempt already reached the provider, it will not be charged twice.',
          {
            amount: formatCurrency(payment.amount, payment.currency || 'USD'),
          },
        )}
        confirmText={
          payment.status === 'failed'
            ? gt('Retry payment')
            : gt('Execute payment')
        }
        loading={executePayment.isPending}
      />

      <ConfirmDialog
        open={removeInvoiceId !== null}
        onClose={() => setRemoveInvoiceId(null)}
        onConfirm={handleRemoveInvoiceConfirm}
        title={gt('Remove invoice from payment?')}
        message={gt(
          'This will unlink the invoice from this payment and recalculate the payment total.',
        )}
        confirmText={gt('Remove invoice')}
        loading={removeInvoice.isPending}
        dangerous
      />

      <ConfirmDialog
        open={confirmAction === 'revoke'}
        onClose={closeConfirm}
        onConfirm={() => {
          revokeApprovalRequest.mutate(
            { paymentId: payment.id },
            { onSuccess: closeConfirm },
          );
        }}
        title={gt('Revoke approval request?')}
        message={gt(
          'Approvers will be notified by email that the request was revoked. You can re-request approval after making changes.',
        )}
        confirmText={gt('Revoke request')}
        loading={revokeApprovalRequest.isPending}
        dangerous
      />

      <SendRemittanceDialog
        open={isRemittanceDialogOpen}
        onOpenChange={setIsRemittanceDialogOpen}
        paymentId={payment.id}
        supplierId={supplierId ?? null}
        supplierName={supplier?.name ?? null}
        isPaid={payment.status === 'paid'}
      />
    </>
  );
}
